import { ROUND_TERMINAL_STATES, promoPayout, worstCase } from '@urbanrats/domain';
import type { GameSelection } from '@urbanrats/contracts';
import type { OpsBet, OpsReward, OpsRound } from './model.ts';

export interface ExposureRow {
  gameRoundId: string;
  gameId: string;
  state: string;
  currency: string;
  scale: number;
  selection: string;
  bets: number;
  stake: bigint;
  /** Total credit owed if this selection wins (accepted quotes: odds, Lightning, boost, cap). */
  payoutIfWins: bigint;
  /** payoutIfWins − cash staked on the whole round in this currency. */
  netIfWins: bigint;
}

/**
 * Open liability per round, selection and currency from accepted bets (never re-priced). Selections of a
 * market are mutually exclusive except the exact side market, which may win together with EXACT.
 */
export function exposure(rounds: readonly OpsRound[], bets: readonly OpsBet[]): ExposureRow[] {
  const open = new Map(rounds.filter((r) => !ROUND_TERMINAL_STATES.has(r.state) && r.state !== 'RESULT_FINAL' && r.state !== 'SETTLING').map((r) => [r.gameRoundId, r]));
  const rows = new Map<string, ExposureRow>();
  const roundCash = new Map<string, bigint>();
  for (const { bet: b } of bets) {
    const r = open.get(b.gameRoundId);
    if (!r || b.state !== 'ACCEPTED' || !b.quote) continue;
    const k = `${b.gameRoundId}|${b.stake.currency}|${b.selection}`;
    const funding = b.funding === 'FREEBET' && b.quote.freebetPolicy ? ({ kind: 'FREEBET', faceValue: b.stake, policy: b.quote.freebetPolicy } as const) : ({ kind: 'CASH' } as const);
    const pay = BigInt(promoPayout({ stake: b.stake, odds: b.quote.odds, lightning: b.quote.lightning, boostBps: b.quote.boostBps, cap: b.quote.payoutCap, funding }, true).credit.amountAtomic);
    const row = rows.get(k) ?? { gameRoundId: b.gameRoundId, gameId: r.gameId, state: r.state, currency: b.stake.currency, scale: b.stake.scale, selection: b.selection, bets: 0, stake: 0n, payoutIfWins: 0n, netIfWins: 0n };
    row.bets++;
    row.stake += BigInt(b.stake.amountAtomic);
    row.payoutIfWins += pay;
    rows.set(k, row);
    const rc = `${b.gameRoundId}|${b.stake.currency}`;
    roundCash.set(rc, (roundCash.get(rc) ?? 0n) + BigInt(b.cashDebit.amountAtomic));
  }
  for (const row of rows.values()) row.netIfWins = row.payoutIfWins - (roundCash.get(`${row.gameRoundId}|${row.currency}`) ?? 0n);
  return [...rows.values()].sort((a, b) => (b.netIfWins > a.netIfWins ? 1 : b.netIfWins < a.netIfWins ? -1 : 0));
}

export interface WorstCaseRow {
  gameRoundId: string;
  gameId: string;
  cameraAlias: string;
  currency: string;
  scale: number;
  /** Selections winning together in the worst outcome (EXACT + EXACT_LOW counts as one outcome). */
  outcome: string[];
  payout: bigint;
  net: bigint;
}

/** Correlated worst case per open round and currency: payouts of selections that win together are added. */
export function worstCaseByRound(rounds: readonly OpsRound[], rows: readonly ExposureRow[], bets: readonly OpsBet[]): WorstCaseRow[] {
  const byRound = new Map(rounds.map((r) => [r.gameRoundId, r]));
  const cash = new Map<string, bigint>();
  for (const { bet: b } of bets) if (b.state === 'ACCEPTED') cash.set(`${b.gameRoundId}|${b.stake.currency}`, (cash.get(`${b.gameRoundId}|${b.stake.currency}`) ?? 0n) + BigInt(b.cashDebit.amountAtomic));
  const groups = new Map<string, ExposureRow[]>();
  for (const r of rows) groups.set(`${r.gameRoundId}|${r.currency}`, [...(groups.get(`${r.gameRoundId}|${r.currency}`) ?? []), r]);
  const out: WorstCaseRow[] = [];
  for (const [k, rs] of groups) {
    const round = byRound.get(rs[0]!.gameRoundId);
    if (!round) continue;
    const w = worstCase(round.manifest.rules, new Map(rs.map((x) => [x.selection as GameSelection, { payoutAtomic: x.payoutIfWins, stakeAtomic: x.stake }])));
    out.push({ gameRoundId: round.gameRoundId, gameId: round.gameId, cameraAlias: round.cameraAlias, currency: rs[0]!.currency, scale: rs[0]!.scale, outcome: w.outcome, payout: w.payoutAtomic, net: w.payoutAtomic - (cash.get(k) ?? 0n) });
  }
  return out.sort((a, b) => (b.net > a.net ? 1 : b.net < a.net ? -1 : 0));
}

export function promoSummary(rewards: readonly OpsReward[], nowIso: string) {
  const by = new Map<string, { currency: string; scale: number; counts: Record<string, number>; outstandingFreebetFace: bigint; expiringSoon: number }>();
  const now = Date.parse(nowIso);
  for (const { item } of rewards) {
    const scale = item.faceValue?.scale ?? 0;
    const row = by.get(item.currency) ?? { currency: item.currency, scale, counts: {}, outstandingFreebetFace: 0n, expiringSoon: 0 };
    if (item.faceValue) row.scale = item.faceValue.scale;
    row.counts[item.state] = (row.counts[item.state] ?? 0) + 1;
    if (item.state === 'GRANTED' && item.kind === 'FREEBET' && item.faceValue) row.outstandingFreebetFace += BigInt(item.faceValue.amountAtomic);
    if (item.state === 'GRANTED' && item.expiresAt && Date.parse(item.expiresAt) - now < 24 * 3600_000) row.expiringSoon++;
    by.set(item.currency, row);
  }
  return [...by.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}
